import { FC, useEffect, useState } from 'react'
import axios from 'axios'
import moment from 'moment'
import { useSearchParams } from 'react-router-dom'
import { CustomCalendar } from 'src/App/AppRouter/Layout/TopBar/TopBarDatePicker/CustomCalendar'
import { getAnalyticsApiHeaders } from 'src/services/analyticsApi'
import { ANALYTICS_API_URL } from 'src/services/constants/urls'
import { CampaignType } from 'src/services/__generated__/api'
import styles from './downloadReportModal.module.scss'

interface DownloadReportModalProps {
  campaigns: CampaignType[]
  onClose: () => void
}

type DateField = 'from' | 'to' | null

const CalendarIcon: FC = () => (
  <svg
    fill="none"
    height="16"
    viewBox="0 0 24 24"
    width="16"
    xmlns="http://www.w3.org/2000/svg"
  >
    <rect
      height="16"
      rx="3"
      stroke="#25262D"
      strokeOpacity="0.4"
      strokeWidth="1.5"
      width="18"
      x="3"
      y="4"
    />
    <path
      d="M3 9h18M8 2v4M16 2v4"
      stroke="#25262D"
      strokeLinecap="round"
      strokeOpacity="0.4"
      strokeWidth="1.5"
    />
  </svg>
)

export const DownloadReportModal: FC<DownloadReportModalProps> = ({
  campaigns,
  onClose,
}) => {
  // Default the date range to the global (top-bar) date filter from the URL.
  const [searchParams] = useSearchParams()
  const parseParamDate = (value: string | null) => {
    if (!value) return null
    const m = moment(value)
    return m.isValid() ? m : null
  }
  const initialFrom = () => parseParamDate(searchParams.get('from'))
  const initialTo = () => parseParamDate(searchParams.get('to'))

  // draft selections (while user is picking)
  const [selectedIds, setSelectedIds] = useState<string[]>([])
  const [fromDate, setFromDate] = useState<moment.Moment | null>(initialFrom)
  const [toDate, setToDate] = useState<moment.Moment | null>(initialTo)
  const [openCalendar, setOpenCalendar] = useState<DateField>(null)

  // confirmed selections (shown as chips after Apply); seeded from the global range
  const [confirmedIds, setConfirmedIds] = useState<string[]>([])
  const [confirmedFrom, setConfirmedFrom] = useState<moment.Moment | null>(
    initialFrom
  )
  const [confirmedTo, setConfirmedTo] = useState<moment.Moment | null>(
    initialTo
  )

  // lock body scroll while modal is open
  useEffect(() => {
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = ''
    }
  }, [])

  const toggleCampaign = (id: string) =>
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
    )

  // helper to keep lint happy (no direct setState in ternary)
  const setSelected = (ids: string[]) => setSelectedIds(ids)

  const allSelected =
    campaigns.length > 0 && selectedIds.length === campaigns.length

  const toggleAll = () =>
    setSelected(
      allSelected ? [] : campaigns.map((c) => c._id ?? '').filter(Boolean)
    )

  const handleCalendarApply = (start: moment.Moment, end: moment.Moment) => {
    if (openCalendar === 'from') setFromDate(start)
    else setToDate(end)
    setOpenCalendar(null)
  }

  const [isDownloading, setIsDownloading] = useState(false)
  const [isDownloadingMention, setIsDownloadingMention] = useState(false)

  const handleDownload = async () => {
    setIsDownloading(true)
    try {
      const params: Record<string, string> = {}
      if (confirmedIds.length) params.campaignIds = confirmedIds.join(',')
      if (confirmedFrom) params.fromDate = confirmedFrom.toISOString()
      if (confirmedTo) params.toDate = confirmedTo.toISOString()

      const response = await axios.get(
        `${ANALYTICS_API_URL}/v1_private/analytics/fetchMentionsReport`,
        {
          headers: getAnalyticsApiHeaders(),
          params,
          responseType: 'blob',
        }
      )

      const disposition = response.headers['content-disposition'] ?? ''
      const match = disposition.match(/filename="?([^"]+)"?/)
      const fileName = match
        ? match[1]
        : `Mentions_Report_${moment().format('YYYY-MM-DD')}.xlsx`

      const url = URL.createObjectURL(new Blob([response.data]))
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', fileName)
      document.body.appendChild(link)
      link.click()
      link.remove()
      URL.revokeObjectURL(url)

      onClose()
    } catch {
      // keep modal open on error so user can retry
    } finally {
      setIsDownloading(false)
    }
  }

  // Exports the selected campaigns + their mention links as an Excel workbook
  // (no charts). Same campaign/date selection as the Excel export above. The
  // richer PDF deck (fetchMentionReportPdf) is kept on the backend for later.
  const handleDownloadMentionReport = async () => {
    setIsDownloadingMention(true)
    try {
      const params: Record<string, string> = {}
      if (confirmedIds.length) params.campaignIds = confirmedIds.join(',')
      if (confirmedFrom) params.fromDate = confirmedFrom.toISOString()
      if (confirmedTo) params.toDate = confirmedTo.toISOString()

      const response = await axios.get(
        `${ANALYTICS_API_URL}/v1_private/analytics/fetchMentionReportExcel`,
        {
          headers: getAnalyticsApiHeaders(),
          params,
          responseType: 'blob',
        }
      )

      const disposition = response.headers['content-disposition'] ?? ''
      const match = disposition.match(/filename="?([^"]+)"?/)
      const fileName = match
        ? match[1]
        : `Mention_Report_${moment().format('YYYY-MM-DD')}.xlsx`

      const url = URL.createObjectURL(new Blob([response.data]))
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', fileName)
      document.body.appendChild(link)
      link.click()
      link.remove()
      URL.revokeObjectURL(url)

      onClose()
    } catch {
      // keep modal open on error so user can retry
    } finally {
      setIsDownloadingMention(false)
    }
  }

  const handleApply = () => {
    setConfirmedIds([...selectedIds])
    setConfirmedFrom(fromDate)
    setConfirmedTo(toDate)
  }

  const handleClearAll = () => {
    setSelectedIds([])
    setFromDate(null)
    setToDate(null)
    setOpenCalendar(null)
    setConfirmedIds([])
    setConfirmedFrom(null)
    setConfirmedTo(null)
  }

  const removeConfirmedCampaign = (id: string) => {
    const next = confirmedIds.filter((i) => i !== id)
    setConfirmedIds(next)
    setSelectedIds(next)
  }

  const formatDate = (d: moment.Moment | null) =>
    d ? d.format('MMM D, YYYY') : ''

  const confirmedCampaigns = campaigns.filter(
    (c) => c._id && confirmedIds.includes(c._id)
  )

  const hasDraftSelection =
    selectedIds.length > 0 || Boolean(fromDate) || Boolean(toDate)
  const hasChips = confirmedCampaigns.length > 0
  const canDownload =
    confirmedCampaigns.length > 0 || Boolean(confirmedFrom || confirmedTo)

  return (
    <div className={styles.overlay} onClick={onClose}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className={styles.header}>
          <h2 className={styles.title}>Filters</h2>
          <button className={styles.closeBtn} onClick={onClose} type="button">
            <svg fill="none" height="20" viewBox="0 0 24 24" width="20">
              <path
                d="M18 6L6 18M6 6l12 12"
                stroke="#25262D"
                strokeLinecap="round"
                strokeWidth="1.5"
              />
            </svg>
          </button>
        </div>

        {/* Body — fixed, no scroll on the whole body */}
        <div className={styles.body}>
          {/* Campaigns */}
          <div className={styles.section}>
            <span className={styles.sectionLabel}>Choose Campaigns:</span>
            <div className={styles.campaignList}>
              <div className={styles.campaignListHeader}>
                <span className={styles.campaignPlaceholder}>Campaign</span>
                <span
                  className={`${styles.selectedCount} ${selectedIds.length > 0 ? styles.selectedCountActive : ''}`}
                >
                  {selectedIds.length} Campaign Selected
                </span>
              </div>
              <div className={styles.campaignOptions}>
                <button
                  className={`${styles.campaignOption} ${allSelected ? styles.campaignOptionSelected : ''}`}
                  onClick={toggleAll}
                  type="button"
                >
                  <span
                    className={`${styles.checkbox} ${allSelected ? styles.checkboxSelected : ''}`}
                  >
                    {allSelected && (
                      <svg
                        fill="none"
                        height="10"
                        viewBox="0 0 12 12"
                        width="10"
                      >
                        <path
                          d="M2 6l3 3 5-5"
                          stroke="#fff"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="1.5"
                        />
                      </svg>
                    )}
                  </span>
                  All Campaigns
                </button>
                {campaigns.map((campaign) => {
                  const id = campaign._id ?? ''
                  const isSelected = selectedIds.includes(id)
                  return (
                    <button
                      className={`${styles.campaignOption} ${isSelected ? styles.campaignOptionSelected : ''}`}
                      key={id}
                      onClick={() => toggleCampaign(id)}
                      type="button"
                    >
                      <span
                        className={`${styles.checkbox} ${isSelected ? styles.checkboxSelected : ''}`}
                      >
                        {isSelected && (
                          <svg
                            fill="none"
                            height="10"
                            viewBox="0 0 12 12"
                            width="10"
                          >
                            <path
                              d="M2 6l3 3 5-5"
                              stroke="#fff"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth="1.5"
                            />
                          </svg>
                        )}
                      </span>
                      {campaign.name}
                    </button>
                  )
                })}
              </div>
            </div>
          </div>

          {/* Date range */}
          <div className={styles.section}>
            <span className={styles.dateHint}>
              Choose an exact date or select a date range.
            </span>
            <div className={styles.dateRow}>
              <div className={styles.dateGroup}>
                <span className={styles.dateLabel}>From</span>
                <button
                  className={`${styles.dateInput} ${openCalendar === 'from' ? styles.dateInputActive : ''}`}
                  onClick={() =>
                    setOpenCalendar(openCalendar === 'from' ? null : 'from')
                  }
                  type="button"
                >
                  <CalendarIcon />
                  <span className={styles.dateInputText}>
                    {formatDate(fromDate)}
                  </span>
                </button>
                {openCalendar === 'from' && (
                  <div className={styles.inlineCalendar}>
                    <CustomCalendar
                      initialEnd={fromDate}
                      initialStart={fromDate}
                      maxDate={toDate}
                      onApply={handleCalendarApply}
                      onClose={() => setOpenCalendar(null)}
                      onReset={() => {
                        setFromDate(null)
                      }}
                      selectionMode="single"
                    />
                  </div>
                )}
              </div>
              <div className={styles.dateGroup}>
                <span className={styles.dateLabel}>To</span>
                <button
                  className={`${styles.dateInput} ${openCalendar === 'to' ? styles.dateInputActive : ''}`}
                  onClick={() =>
                    setOpenCalendar(openCalendar === 'to' ? null : 'to')
                  }
                  type="button"
                >
                  <CalendarIcon />
                  <span className={styles.dateInputText}>
                    {formatDate(toDate)}
                  </span>
                </button>
                {openCalendar === 'to' && (
                  <div className={styles.inlineCalendar}>
                    <CustomCalendar
                      initialEnd={toDate}
                      initialStart={toDate}
                      minDate={fromDate}
                      onApply={handleCalendarApply}
                      onClose={() => setOpenCalendar(null)}
                      onReset={() => {
                        setToDate(null)
                      }}
                      selectionMode="single"
                    />
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Chips — only shown after Apply, only this area scrolls */}
          {hasChips && (
            <div className={styles.chipsArea}>
              {confirmedCampaigns.map((c) => (
                <div className={styles.chip} key={c._id}>
                  <span className={styles.chipLabel}>
                    <span className={styles.chipKey}>Campaigns: </span>
                    {c.name}
                  </span>
                  <button
                    className={styles.chipRemove}
                    onClick={() => removeConfirmedCampaign(c._id ?? '')}
                    type="button"
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className={styles.footer}>
          <button
            className={styles.clearBtn}
            onClick={handleClearAll}
            type="button"
          >
            Clear All
          </button>
          {!canDownload ? (
            <button
              className={styles.applyBtn}
              disabled={!hasDraftSelection}
              onClick={handleApply}
              type="button"
            >
              Apply
            </button>
          ) : (
            <div className={styles.downloadActions}>
              <button
                className={styles.secondaryBtn}
                disabled={isDownloading || isDownloadingMention}
                onClick={handleDownload}
                type="button"
              >
                {isDownloading ? 'Downloading...' : 'Download Excel'}
              </button>
              <button
                className={styles.applyBtn}
                disabled={isDownloading || isDownloadingMention}
                onClick={handleDownloadMentionReport}
                type="button"
              >
                {isDownloadingMention
                  ? 'Downloading…'
                  : 'Download Mention Reports'}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
